<template>
    <!-- Only wrap in tooltip container if there's actual tooltip content -->
    <div
        v-if="$slots['tooltip-content']"
        class="tooltip-trigger"
        :style="{ cursor: 'pointer' }"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
        ref="trigger"
    >
        <!-- Trigger slot -->
        <slot name="tooltip-trigger"></slot>

        <!-- Content slot -->
        <div
            ref="tooltip"
            :class="['tooltip-text', { below: isBelow }]"
            :style="tooltipStyle"
        >
            <slot name="tooltip-content"></slot>
        </div>
    </div>

    <!-- If no tooltip content, render trigger directly without wrapper -->
    <slot v-else name="tooltip-trigger"></slot>
</template>

<script>
export default {
    name: 'ToolTip',
    data() {
        return {
            isBelow: false,
            tooltipStyle: {
                position: 'fixed',
                visibility: 'hidden',
                opacity: 0,
                top: '0px',
                left: '0px',
                zIndex: 1000,
            },
        };
    },
    methods: {
        showTooltip() {
            if (!this.$refs.trigger || !this.$refs.tooltip) return;

            this.tooltipStyle = {
                ...this.tooltipStyle,
                visibility: 'hidden',
                opacity: 0,
                display: 'flex',
            };

            this.$nextTick(() => {
                const triggerRect = this.$refs.trigger.getBoundingClientRect();

                // Calculate available space from center of triggerRect
                const triggerCenterX = triggerRect.left + triggerRect.width / 2;
                const viewportWidth = window.innerWidth;
                const padding = 16; //padding on left and right of tooltip

                // Calculate available space on left and right from triggerRect center
                const availableLeft = triggerCenterX - padding;
                const availableRight = viewportWidth - triggerCenterX - padding;

                const totalAvailableWidth = Math.min(
                    viewportWidth - padding * 2,
                    availableLeft + availableRight
                );

                // Convert to rem (assuming 16px = 1rem)
                const maxAvailableWidthRem = totalAvailableWidth / 16;

                // Use the smaller of default max-width (8.9375rem) or available width
                const dynamicMaxWidth = Math.min(8.9375, maxAvailableWidthRem);

                // Apply the dynamic max-width
                this.tooltipStyle = {
                    ...this.tooltipStyle,
                    maxWidth: `${dynamicMaxWidth}rem`,
                };

                // Wait for reflow with new max-width
                this.$nextTick(() => {
                    const tooltipRect =
                        this.$refs.tooltip.getBoundingClientRect();

                    // calculate top and left position of tooltip
                    const top = triggerRect.top - tooltipRect.height - 10;
                    const left =
                        triggerRect.left +
                        triggerRect.width / 2 -
                        tooltipRect.width / 2;

                    // check if tooltip fits within viewport bounds
                    const adjustedLeft = Math.max(
                        padding,
                        Math.min(
                            left,
                            viewportWidth - tooltipRect.width - padding
                        )
                    );

                    // Calculate arrow position to point at trigger center
                    const arrowLeft = triggerCenterX - adjustedLeft;
                    const arrowLeftPercentage =
                        (arrowLeft / tooltipRect.width) * 100;

                    // Clamp arrow position to stay within tooltip bounds
                    const clampedArrowPercentage = Math.max(
                        6, // 6px from left edge (arrow width)
                        Math.min(
                            arrowLeftPercentage,
                            100 - (12 / tooltipRect.width) * 100
                        ) // 6px from right edge
                    );

                    // show tooltip if it fits above, otherwise show below
                    const showBelow = top < 8;
                    const finalTop = showBelow
                        ? triggerRect.bottom + 10
                        : Math.max(8, top);
                    this.isBelow = showBelow;

                    this.tooltipStyle = {
                        position: 'fixed',
                        visibility: 'visible',
                        opacity: 1,
                        top: `${finalTop}px`,
                        left: `${adjustedLeft}px`,
                        zIndex: 1000,
                        display: 'flex',
                        maxWidth: `${dynamicMaxWidth}rem`,
                        '--arrow-left': `${clampedArrowPercentage}%`,
                    };
                });
            });
        },

        hideTooltip() {
            this.tooltipStyle = {
                ...this.tooltipStyle,
                visibility: 'hidden',
                opacity: 0,
            };
            this.isBelow = false;
        },
    },
};
</script>

<style scoped>
.tooltip-trigger {
    position: relative;
    display: inline-block;
    width: fit-content;
}

.tooltip-text {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--black);
    color: white;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    font-size: 0.875rem;
    line-height: 1.35;
    font-weight: 400;
    font-family: Inter, sans-serif;
    min-width: 4rem;
    max-width: 8.9375rem;
    word-wrap: break-word;
    white-space: normal;
    text-align: center;
    transition: opacity 0.2s;
    pointer-events: none;
}

/* Arrow pointing down */
.tooltip-text::after {
    content: '';
    position: absolute;
    top: 100%;
    left: var(--arrow-left, 50%);
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: var(--black) transparent transparent transparent;
}

/* Arrow pointing up */
.tooltip-text.below::after {
    top: -12px;
    left: var(--arrow-left, 50%);
    border-color: transparent transparent var(--black) transparent;
}
</style>
